import {useState, useEffect, useMemo} from 'react';
import {walletAudienceToForm} from '../utils/walletAudience';
import {investmentAudienceToForm} from '../utils/investmentAudience';
import {Form} from 'antd';
import moment from 'moment';
import {readAnnouncementId} from '../utils/notificationDataTransformers';
import {emptyRecipients} from 'components/notificationRecipientSelect/recipientCsv';

/**
 * The backend stores the "specific user(s)" audience as `SPECIFIC_USERS`,
 * but the form's audience options use `USERS`. Map between the two so the
 * correct option is selected and the user-select field is revealed in edit mode.
 */
const mapAudienceToForm = category =>
  category === 'SPECIFIC_USERS' ? 'USERS' : category;

/**
 * Multiple-mode antd Selects expect an array. A scalar `null`/`undefined`
 * gets coerced into an empty removable tag (e.g. `[null]`), which shows up as
 * a blank, pre-selected chip. Normalize so empty values render as a placeholder
 * and non-array values are wrapped.
 */
const toMultiValue = value => {
  if (Array.isArray(value)) return value;
  return value == null ? undefined : [value];
};

/**
 * Custom hook to manage notification form state using Ant Design Form
 * Works for both create and update modes
 */
export const useNotificationForm = (initialData = null, isEditMode = false) => {
  const [form] = Form.useForm();

  // Only UI state that's not part of form fields
  const [activeTab, setActiveTab] = useState('APP');
  const [emailPreviewImage, setEmailPreviewImage] = useState('');

  // Metadata state (not form fields)
  const [opportunityMetaData, setOpportunityMetaData] = useState({});
  const [reportMetaData, setReportMetaData] = useState({});
  const [companyMetaData, setCompanyMetaData] = useState({});
  const [groupMetaData, setGroupMetaData] = useState({});
  // The whole recipient set for the "specific users" audience: the picker's
  // selections plus anything read from an uploaded CSV. `sendNotificationToUsers`
  // accepts `userIds` and `userEmails` together, so both halves travel as one
  // value and are split at submit time by `buildRecipientPayload`.
  const [recipients, setRecipients] = useState(emptyRecipients());

  // Translation state (not form fields)
  const [translatedLanguage, setTranslatedLanguage] = useState([]);
  const [enableChangeTranslation, setEnableChangeTranslation] = useState(true);
  const [openDeleteTranslationModal, setOpenDeleteTranslationModal] =
    useState(false);
  const [selectedTranslation, setSelectedTranslation] = useState('');

  // WhatsApp template state
  const [selectedWhatsappTemplate, setSelectedWhatsappTemplate] = useState({});

  // Recipient user IDs when editing a "specific users" notification. Only
  // resolved ids are persisted, so a notification originally sent from an
  // uploaded sheet reopens as users. NotificationRecipientSelect resolves
  // these IDs to full user records on its own.
  const defaultUserIds = useMemo(() => {
    const notification = Array.isArray(initialData)
      ? initialData[0]
      : initialData;
    return (notification?.users || []).map(user => user?.id).filter(Boolean);
  }, [initialData]);

  // Watch form fields that need to trigger side effects
  const category = Form.useWatch('category', form) || [];
  const notificationCategory = Form.useWatch('category', form) || [];
  const type = Form.useWatch('type', form) || [];
  const title = Form.useWatch('title', form) || '';
  const body = Form.useWatch('body', form) || '';
  const emailBody = Form.useWatch('emailBody', form) || '';
  const emailHeader = Form.useWatch('emailHeader', form) || '';
  const emailImage = Form.useWatch('emailBanner', form) || '';
  const addDisclosure = Form.useWatch('addDisclosure', form) || false;
  const disclosureType = Form.useWatch('disclosureType', form) || 'NONE';
  const frequency = Form.useWatch('frequency', form) || '';
  const date = Form.useWatch('date', form);
  const expires = Form.useWatch('expires', form) || null;
  const audience = Form.useWatch('audience', form) || 'ALL_USERS';
  const baseLanguage = Form.useWatch('baseLanguage', form) || 'EN';
  const linkShouldOpenInTheApp =
    Form.useWatch('linkShouldOpenInTheApp', form) || false;

  // Initialize form with data if in edit mode
  useEffect(() => {
    if (isEditMode && initialData) {
      const notification = Array.isArray(initialData)
        ? initialData[0]
        : initialData;

      // Determine active tab based on category
      let tab = 'APP';
      if (notification.category) {
        const categories = Array.isArray(notification.category)
          ? notification.category
          : [notification.category];
        if (categories.includes('EMAIL')) {
          tab = 'EMAIL';
        } else if (categories.includes('WHATSAPP')) {
          tab = 'WHATSAPP';
        }
      }
      setActiveTab(tab);

      // Set email preview image if exists
      if (notification.emailBanner) {
        setEmailPreviewImage(notification.emailBanner);
      }

      // Set translated language if exists
      if (notification.multilang) {
        setTranslatedLanguage(
          notification.multilang?.map(data => ({
            title: data.title,
            message: data.body,
            language: data.language,
          })),
        );
      }

      // Transform audience data for form
      const audienceData = notification.audience || {};

      // Transform signup date fields
      let signupDate = null;
      let from = null;
      let to = null;
      let durationUnit = null;
      let durationValue = null;
      if (audienceData.signupDateRange) {
        signupDate = 'RANGE';
        from = audienceData.signupDateRange.from
          ? moment(audienceData.signupDateRange.from)
          : null;
        to = audienceData.signupDateRange.to
          ? moment(audienceData.signupDateRange.to)
          : null;
      } else if (audienceData.signupDateDuration) {
        signupDate = 'DURATION';
        durationUnit = audienceData.signupDateDuration.unit;
        durationValue = audienceData.signupDateDuration.value;
      }

      // Transform order transaction date
      let orderFrom = null;
      let orderTo = null;
      if (audienceData.orderTransactionDate) {
        orderFrom = audienceData.orderTransactionDate.from
          ? moment(audienceData.orderTransactionDate.from)
          : null;
        orderTo = audienceData.orderTransactionDate.to
          ? moment(audienceData.orderTransactionDate.to)
          : null;
      }

      // Set form values
      form.setFieldsValue({
        baseLanguage: notification.language || 'EN',
        category: notification.category,
        class: notification.class,
        type: notification.type,
        title: notification.title,
        template: notification.whatsappTemplateName,
        body: notification.body,
        emailBody: notification.body, // For email, body is emailBody
        emailHeader: notification.emailHeader,
        emailBanner: notification.emailBanner,
        addDisclosure: notification.addDisclosure || false,
        disclosureType: notification.disclosureType || 'NONE',
        disclosure: notification.disclosure,
        date: notification.schedule?.date
          ? moment(notification.schedule.date)
          : null,
        frequency: notification.schedule?.frequency || '',
        selectedDays: notification.schedule?.selectedDays || [],
        expires: notification.schedule?.expires
          ? moment(notification.schedule.expires)
          : null,
        audience: mapAudienceToForm(audienceData.category) || 'ALL_USERS',
        senderEmail: notification.from,
        replyEmail: notification.replyTo,
        externalLink: notification.externalLink,
        linkShouldOpenInTheApp: notification.shouldOpenInApp || false,
        // Instrument v2 destination. This hook seeds the edit form directly
        // rather than going through transformNotificationToFormData, so the
        // hydration added there never reached the picker — the instrument
        // came back empty and a save would have dropped the destination.
        instrumentId: notification.instrumentId,
        // Optional announcement reference, stored top-level or under
        // `metadata.announcementID`. Seeded here for the same reason as the
        // instrument above: this hook fills the edit form directly, so a
        // missing hydration would silently drop the reference on save.
        announcementId: readAnnouncementId(notification),
        // Audience instrument filters, prefixed so they cannot collide with
        // the destination field above.
        // Audience filter fields
        accredited: audienceData.accredited,
        activeInvestors:
          audienceData.activeInvestors || audienceData.activeInvestor,
        verifiedInvestors: audienceData.verifiedInvestors,
        latestVisit: audienceData.lastAppVisit,
        profileCompletion: audienceData.profileCompletion,
        isDabaPro: audienceData.isDabaPro,
        isKycApproved: toMultiValue(audienceData.isKycApproved),
        isDocumentCheckApproved: toMultiValue(
          audienceData.isDocumentCheckApproved,
        ),
        isAmlApproved: toMultiValue(audienceData.isAmlApproved),
        riskProfile: toMultiValue(audienceData.riskProfile),
        // Citizenship fields
        countryOfResidence: toMultiValue(audienceData.country),
        state: toMultiValue(audienceData.state),
        city: toMultiValue(audienceData.city),
        countryOfBirth: toMultiValue(audienceData.countryOfBirth),
        countryOfCitizenship: toMultiValue(audienceData.countryOfCitizenship),
        visaType: toMultiValue(audienceData.visaType),
        citizenStatus: toMultiValue(audienceData.citizenStatus),
        // Financial profile fields
        netIncome: toMultiValue(audienceData.annualNetIncome),
        taxFilingStatus: toMultiValue(audienceData.taxFilingStatus),
        employmentStatus: toMultiValue(audienceData.employmentStatus),
        estimatedNetWorth: toMultiValue(audienceData.estimatedNetWorth),
        // Investor profile fields
        investmentExperience: toMultiValue(audienceData.investmentExperience),
        investmentPreference: toMultiValue(audienceData.investmentPreference),
        investmentHorizon: toMultiValue(audienceData.investmentHorizon),
        investmentAffiliation: toMultiValue(audienceData.affiliations),
        investmentGoals: toMultiValue(audienceData.investmentGoals),
        investmentStrategies: toMultiValue(
          audienceData.recommendedPortfolioStrategy,
        ),
        // Signup date fields
        signupDate,
        from,
        to,
        durationUnit,
        durationValue,
        // Order filter fields
        orderFrom,
        orderTo,
        orderStatus: toMultiValue(audienceData.orderStatus),
        ...investmentAudienceToForm(audienceData),
        isOrderOnHold: audienceData.isOrderOnHold,
        assetClass: audienceData.assetClass,
        assetId: audienceData.assetId,
        // Wallet activity filters
        ...walletAudienceToForm(audienceData),
        // Campaign fields (if applicable)
        liveCampaignId: notification.audience?.campaign,
        testCampaignId: notification.audience?.campaign,
      });
    }
  }, [initialData, isEditMode, form]);

  return {
    form,
    // Form field values (watched from form)
    category,
    notificationCategory,
    type,
    title,
    body,
    emailBody,
    emailHeader,
    emailImage,
    addDisclosure,
    disclosureType,
    frequency,
    date,
    expires,
    audience,
    baseLanguage,
    linkShouldOpenInTheApp,
    // UI state
    activeTab,
    setActiveTab,
    emailPreviewImage,
    setEmailPreviewImage,
    // Metadata state
    opportunityMetaData,
    setOpportunityMetaData,
    reportMetaData,
    setReportMetaData,
    companyMetaData,
    setCompanyMetaData,
    groupMetaData,
    setGroupMetaData,
    recipients,
    setRecipients,
    defaultUserIds,
    // Translation state
    translatedLanguage,
    setTranslatedLanguage,
    enableChangeTranslation,
    setEnableChangeTranslation,
    openDeleteTranslationModal,
    setOpenDeleteTranslationModal,
    selectedTranslation,
    setSelectedTranslation,
    // WhatsApp state
    selectedWhatsappTemplate,
    setSelectedWhatsappTemplate,
  };
};
